import { useEffect, useState } from "react";
import API from "../lib/api";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { FileText, Eye, DownloadSimple, PencilSimple, Plus, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import JobFormFillDialog from "./JobFormFillDialog";

/* Checkbox list of job form templates — used on create / edit dialogs for every job type. */
export function JobFormsPicker({ value = [], onChange, hint = "Select job sheets the supplier or operative must complete on site", testId = "job-forms-picker" }) {
  const [forms, setForms] = useState([]);
  useEffect(() => { API.get("/job-forms").then(({ data }) => setForms(data.forms || [])).catch(() => {}); }, []);
  if (!forms.length) return null;
  const toggle = (id, on) => onChange(on ? [...value, id] : value.filter((x) => x !== id));
  return (
    <div data-testid={testId}>
      <Label>Job Forms</Label>
      <p className="text-[10px] text-muted-foreground mb-1">{hint}</p>
      <div className="space-y-1.5 mt-1 max-h-32 overflow-y-auto border border-border rounded-md p-2">
        {forms.map((f) => (
          <label key={f.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-2 py-1 rounded">
            <input type="checkbox" checked={value.includes(f.id)} onChange={(e) => toggle(f.id, e.target.checked)} className="rounded" data-testid={`${testId}-${f.id}`} />
            <span>{f.title}</span>
            <span className="text-[10px] text-muted-foreground ml-auto">{f.fields?.length || 0} fields</span>
          </label>
        ))}
      </div>
    </div>
  );
}

/* Job Sheets panel for a job / emergency: assigned templates, submissions (view / PDF), fill-in, and (admins) add/remove. */
export default function JobFormsPanel({ jobId, jobFormIds = [], canEdit = false, canFill = true, onFormIdsChange, testId = "job-forms-panel" }) {
  const [templates, setTemplates] = useState([]);
  const [subs, setSubs] = useState([]);
  const [fill, setFill] = useState(null);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState(null);

  const load = () => {
    API.get(`/jobs/${jobId}/form-submissions`).then(({ data }) => setSubs(data.submissions || [])).catch(() => setSubs([]));
  };
  useEffect(() => { API.get("/job-forms").then(({ data }) => setTemplates(data.forms || [])).catch(() => {}); }, []);
  useEffect(() => { if (jobId) load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const assigned = templates.filter((t) => jobFormIds.includes(t.id));
  const available = templates.filter((t) => !jobFormIds.includes(t.id));

  const downloadPdf = async (s) => {
    try {
      const res = await API.get(`/jobs/${jobId}/form-submissions/${s.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `${s.form_title || "form"}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download PDF"); }
  };

  return (
    <div className="border border-border rounded-md p-4" data-testid={testId}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1"><FileText size={12} /> Job Sheets</h3>
        {canEdit && available.length > 0 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAdding((a) => !a)} data-testid={`${testId}-add-toggle`}><Plus size={12} className="mr-1" /> Add form</Button>
        )}
      </div>
      {adding && (
        <div className="mb-3 border border-dashed border-border rounded p-2 space-y-1" data-testid={`${testId}-add-list`}>
          {available.map((t) => (
            <button key={t.id} type="button" onClick={() => { onFormIdsChange?.([...jobFormIds, t.id]); setAdding(false); }} className="w-full text-left text-xs px-2 py-1 rounded hover:bg-muted flex items-center justify-between" data-testid={`${testId}-add-${t.id}`}>
              <span>{t.title}</span><span className="text-[10px] text-muted-foreground">{t.fields?.length || 0} fields</span>
            </button>
          ))}
        </div>
      )}
      {assigned.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid={`${testId}-empty`}>No job sheets attached{canEdit ? " — add one so the operative fills it in on site." : "."}</p>
      ) : (
        <ul className="space-y-2">
          {assigned.map((t) => {
            const done = subs.filter((s) => s.form_id === t.id);
            return (
              <li key={t.id} className="rounded border border-border p-2.5" data-testid={`${testId}-form-${t.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{t.title}</p>
                    <p className="text-[10px] text-muted-foreground">{t.fields?.length || 0} fields · {done.length ? `${done.length} submission${done.length === 1 ? "" : "s"}` : "not yet completed"}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canFill && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setFill(t)} data-testid={`${testId}-fill-${t.id}`}><PencilSimple size={12} className="mr-1" /> Fill in</Button>}
                    {canEdit && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground" title="Remove from this job" onClick={() => onFormIdsChange?.(jobFormIds.filter((id) => id !== t.id))} data-testid={`${testId}-remove-${t.id}`}><X size={12} /></Button>}
                  </div>
                </div>
                {done.length > 0 && (
                  <ul className="mt-2 space-y-1">
                    {done.map((s) => (
                      <li key={s.id} className="flex items-center justify-between gap-2 text-[11px] bg-muted/40 rounded px-2 py-1" data-testid={`${testId}-submission-${s.id}`}>
                        <span>Submitted by <strong>{s.submitted_by_name}</strong> · {s.submitted_at ? new Date(s.submitted_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                        <span className="flex items-center gap-1">
                          <button type="button" onClick={() => setView(view?.id === s.id ? null : s)} className="text-primary hover:underline inline-flex items-center gap-0.5"><Eye size={11} /> {view?.id === s.id ? "Hide" : "View"}</button>
                          <button type="button" onClick={() => downloadPdf(s)} className="text-primary hover:underline inline-flex items-center gap-0.5"><DownloadSimple size={11} /> PDF</button>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                {view && view.form_id === t.id && (
                  <div className="mt-2 border border-border rounded p-2 space-y-1 text-xs" data-testid={`${testId}-submission-view`}>
                    {(t.fields || []).map((f) => {
                      const v = view.responses?.[f.id];
                      const text = Array.isArray(v) ? v.map((x) => (typeof x === "object" ? x.name || "photo" : x)).join(", ") : typeof v === "string" && v.startsWith("data:image") ? "(signature)" : String(v ?? "—");
                      return <p key={f.id}><span className="text-muted-foreground">{f.label}:</span> {text}</p>;
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {fill && <JobFormFillDialog jobId={jobId} form={fill} open={!!fill} onClose={() => setFill(null)} onSubmitted={() => { setFill(null); load(); }} />}
    </div>
  );
}
